import { ComboboxGroup, ComboboxGroupLabel, ComboboxList } from '@ariakit/react'
import React, { FC, useEffect } from 'react'
import { LocaleTextHook } from '@moonpig/web-core-locale-text'
import { styled } from '@moonpig/launchpad-utils'
import { Box, Link } from '@moonpig/launchpad-components'
import { system as s } from '@moonpig/launchpad-system'
import { groupItemsByHeaders } from './groupItemsByHeaders'
import { SectionHeader } from '../SectionHeader/SectionHeader'
import { mapItems } from './mapItems'
import {
  OnClearRecentSearches,
  OnRemindersVisible,
  OnRemoveRecentSearch,
  OnSubmit,
  SearchRow,
} from '../types'
import { NAV_TEXT } from '../../locale'
import { DepartmentType } from '../departmentMap'
import { SkeletonList } from './SkeletonList'

const StyledClearAllButton = styled(Link)`
  text-decoration: none;
  ${s({
    color: 'colorInteractionTextLink',
    typography: 'typeButtonLabel',
    fontSize: '12px',
    mr: 6,
  })}
`

const StyledComboboxList = styled(ComboboxList)`
  ${s({
    pt: 5,
  })}
`

type Props = {
  flatList: SearchRow[]
  firstListItemlRef: React.RefObject<HTMLDivElement | null>
  onSubmit: OnSubmit
  inputValue: string
  onClearRecentSearches: OnClearRecentSearches
  localiseText: LocaleTextHook<typeof NAV_TEXT>
  onRemoveRecentSearch: OnRemoveRecentSearch
  activeDepartment: DepartmentType
  onRemindersVisible: OnRemindersVisible
  suggestionsLoading?: boolean
}

export const SectionedList: FC<Props> = ({
  flatList,
  firstListItemlRef,
  onSubmit,
  inputValue,
  onClearRecentSearches,
  localiseText,
  onRemoveRecentSearch,
  activeDepartment,
  onRemindersVisible,
  suggestionsLoading,
}) => {
  const groups = groupItemsByHeaders(flatList)
  const remindersVisible = groups.some(group =>
    group.items.some(item => item.kind === 'reminder'),
  )

  useEffect(() => {
    if (remindersVisible) {
      onRemindersVisible()
    }
  }, [onRemindersVisible, remindersVisible])

  const listContent = groups
    .map((group, groupIndex) => {
      const needsClearAllButton =
        group.header &&
        group.header.title === 'RECENTLY SEARCHED' &&
        group.items.length > 1
      const isInspirationGroup = group.items[0].kind === 'inspiration'
      return (
        <ComboboxGroup key={group.header?.id || `group-${groupIndex}`}>
          {group.header && (
            <ComboboxGroupLabel aria-hidden={false}>
              <SectionHeader
                title={group.header.title}
                link={
                  needsClearAllButton && (
                    <StyledClearAllButton
                      tabIndex={0}
                      onClick={onClearRecentSearches}
                      data-testid="lp-nav-search-recent-clear"
                      aria-label={localiseText(
                        'search.clear_all_recent_searches_label',
                      )}
                    >
                      {localiseText('search.clear_recent_searches')}
                    </StyledClearAllButton>
                  )
                }
              />
            </ComboboxGroupLabel>
          )}
          <Box pl={isInspirationGroup ? 5 : 0} pb={isInspirationGroup ? 6 : 2}>
            {mapItems({
              items: group.items,
              firstListItemlRef,
              groupIndex,
              onSubmit,
              inputValue,
              onRemoveRecentSearch,
            })}
          </Box>
        </ComboboxGroup>
      )
    })
    .filter(Boolean)

  return (
    <StyledComboboxList>
      <Box
        role="tabpanel"
        id="suggestions-panel"
        aria-labelledby={`tab-${activeDepartment}`}
      >
        {suggestionsLoading ? <SkeletonList /> : listContent}
      </Box>
    </StyledComboboxList>
  )
}
